import TextField, { type TextFieldProps } from "@mui/material/TextField";
import { useDebouncedCallback } from "core/hooks/useDebouncedCallback";
import type { ComponentProps, Ref } from "react";
import {
  type Control,
  type FieldPath,
  type FieldValues,
  type RegisterOptions,
  useController,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import PhoneInput from "react-phone-number-input/input";

export type PhoneCountryCode = Exclude<
  ComponentProps<typeof PhoneInput>["country"],
  undefined
>;

interface ReactHookTelephoneInputV2Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends TextFieldProps<"standard"> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  required?: boolean;
  defaultValue?: ComponentProps<typeof PhoneInput>["defaultValue"];
  country?: PhoneCountryCode;
  default?: PhoneCountryCode;
  ref?: Ref<HTMLInputElement>;
}
function TextFieldExposedRef({
  ref,
  ...props
}: {
  ref: Ref<HTMLInputElement>;
}) {
  return <TextField {...props} inputRef={ref} />;
}

export function ReactHookTelephoneInputV2<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  rules,
  required,
  defaultValue,
  country = "CA",
  ...textFieldProps
}: ReactHookTelephoneInputV2Props<TFieldValues, TName>) {
  const { t } = useTranslation();
  const requiredErrorMessage = t("Next:Core:FieldRequiredValidationMessage");
  const invalidPhoneNumberMessage = t(
    "Next:Core:ReactHookTelephoneInputV2.invalidPhoneNumberMessage"
  );
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: {
      ...rules,
      required: required && requiredErrorMessage,
      validate: (value: string) => {
        if (!required || isPossiblePhoneNumber(value)) {
          return true;
        }
        return invalidPhoneNumberMessage;
      },
    },
  });
  // this is a bit hacky, but required until this issue is resolved: https://github.com/catamphetamine/react-phone-number-input/issues/441
  const setValue = useDebouncedCallback(onChange, [onChange], 100);
  const labelPrefix = required ? "* " : "";
  const actualLabel = `${labelPrefix}${label}`;
  return (
    <PhoneInput
      country={country}
      defaultValue={defaultValue}
      control={control}
      onChange={setValue as any}
      value={value as any}
      name={name}
      label={actualLabel}
      inputComponent={TextFieldExposedRef as any}
      error={Boolean(error)}
      helperText={error?.message}
      {...textFieldProps}
    />
  );
}
